
import { Component, computed, input, linkedSignal, output, ChangeDetectionStrategy } from '@angular/core';
import { form } from '@angular/forms/signals';
import { ProjectCommentsDto, ProjectDto, ProjectUpdateRequestDto } from '../../core/mceapi.client.generated';
import { DebugPanelComponent } from '../../shared/components/debug-panel/debug-panel.component';
import { MceSaveCancelComponent } from '../../shared/components/mce-save-cancel/mce-save-cancel.component';
import { MceSaveCancelEvent } from '../../shared/components/mce-save-cancel/mce-save-cancel.component';
import { MceSaveCancelConfig } from '../../shared/components/mce-save-cancel/mce-save-cancel.component';
import { invalidFields } from '../../shared/functions/forms.helpers';
import { Option } from '../../shared/functions/functional';
import { ProjectBranding, ProjectBrandingComponent, builldProjectBrandingSchema, createNewLogoImageData64, createProjectBranding, } from './branding/project-edit-branding.component';
import { ProjectComments, ProjectCommentsComponent, buildProjectCommentsSchema, createProjectComments, createProjectCommentsDto } from './comments/project-edit-comments.component';
import { ProjectDefaultValues, ProjectDefaultValuesComponent, buildProjectDefaultValuesSchema, createProjectDefaultValues, createProjectReferenceValuesDto } from './default-values/project-edit-default-values.component';
import { ProjectDescription, ProjectDescriptionComponent, buildProjectDescriptionSchema, createProjectDescription } from './description/project-edit-description.component';

interface EditProject {
  description: ProjectDescription,
  defaultValues: ProjectDefaultValues,
  branding: ProjectBranding,
  comments: ProjectComments
}


@Component({
  selector: 'project-edit',
  imports: [DebugPanelComponent,
    MceSaveCancelComponent, ProjectCommentsComponent, 
    ProjectDescriptionComponent, ProjectDefaultValuesComponent, ProjectBrandingComponent],
    templateUrl: './project-edit.component.html'
})
export class ProjectEditComponent {

  project = input.required<ProjectDto>();
  comments = input.required<Option<ProjectCommentsDto>>();
  showDebugPanel = input(false)

  saveClick = output<ProjectUpdateRequestDto>();
  cancelClick = output();

  readonly model = linkedSignal<EditProject>(() => {
    return {
      description: createProjectDescription(this.project())(),
      defaultValues: createProjectDefaultValues(this.project())(),
      branding: createProjectBranding(this.project())(),
      comments: createProjectComments(this.project().uid ?? '', this.comments())()
    }
  });

  readonly form = form(this.model, s => {
    buildProjectDescriptionSchema(s.description);
    buildProjectDefaultValuesSchema(s.defaultValues);
    builldProjectBrandingSchema(s.branding);
    buildProjectCommentsSchema(s.comments);
  });

  readonly saveCancelConfig = computed<MceSaveCancelConfig>(() => ({
    save: { show: true, enable: this.form().valid() && this.form().dirty() },
    cancel: { show: true, enable: true },
    invalidFields: this.form().dirty() ? invalidFields(this.form, 'root') : []
  }));


  onSave() {

    let project: ProjectDto = {
      ...this.project(),
      projectNumber: this.form.description.projectNumber().value(),
      projectName: this.form.description.projectName().value(),
      country: this.form.defaultValues.country().value(),
      referenceValues: createProjectReferenceValuesDto(this.form.defaultValues)(),
    };

    this.saveClick.emit({
      project: project,
      logo64Data: createNewLogoImageData64(this.form.branding)(),
      comments: createProjectCommentsDto(this.form.comments)()
    });
  }

  onCancel() {
    this.cancelClick.emit();
  }

  onSaveCancelAction(event: MceSaveCancelEvent): void {
    if (event.type === 'save') {
      this.onSave();
      return;
    }

    if (event.type === 'cancel') {
      this.onCancel();
    }
  }

}
